import { Injectable, OnDestroy } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil, take, distinctUntilChanged, map } from 'rxjs/operators';

import { State } from '@completion/reducers';
import { getCurrentProject } from '@completion/selectors';
import { Project } from '@completion/models';
import { PunchNotification, StompWebSocketService } from '../../services/stomp-websocket.service';
import { NotificationService } from '../../services/notification.service';
import { LoadPunchById } from '@completion/actions';
import { env } from '../../../../environments/env';

@Injectable()
export class PunchNotificationEffect implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private hasShownSubscriptionToastForProject = new Set<number>();

  constructor(
    _actions$: Actions,
    private readonly store: Store<State>,
    private readonly notifications: NotificationService,
    private readonly stompService: StompWebSocketService,
  ) {
    if (!env.get().featureFlags.stomp) {
      return;
    }

    let currentProjectId: number | null = null;

    this.store
      .select(getCurrentProject)
      .pipe(
        filter((p): p is Project => !!p),
        // Project object reference can change (ProjectsLoadSuccess/ChangeProject cascades) without the
        // project actually changing. Without this, every such emission re-checked connectionState$ and
        // called stompService.connect() again, which - combined with a backend that rejects the
        // handshake (1002) - produced an endless connect/close loop that pegged the main thread.
        map((project) => project.id),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((projectId) => {
        currentProjectId = projectId;

        this.stompService.subscribeToProjectPunches(projectId);

        this.stompService.connectionState$.pipe(take(1)).subscribe((state) => {
          if (state === 'DISCONNECTED') {
            this.stompService.connect().catch(() => {});
            return;
          }

          if (state === 'CONNECTED' && !this.hasShownSubscriptionToastForProject.has(projectId)) {
            this.hasShownSubscriptionToastForProject.add(projectId);
            this.notifications.info(`Listening for punch updates in project ${projectId}`, 'Punch notifications enabled');
          }
        });
      });

    this.stompService.connectionState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      if (state === 'CONNECTED' && currentProjectId && !this.hasShownSubscriptionToastForProject.has(currentProjectId)) {
        this.hasShownSubscriptionToastForProject.add(currentProjectId);
        this.notifications.info(`Listening for punch updates in project ${currentProjectId}`, 'Punch notifications enabled');
      }
    });

    this.stompService.incomingMessages$.pipe(takeUntil(this.destroy$)).subscribe((msg) => {
      try {
        const notification: PunchNotification = JSON.parse(msg.body);
        if (notification.projectId !== currentProjectId) {
          return;
        }

        // Silently rehydrate the punch in store so any visible user-punch tables get the latest data
        this.store.dispatch(new LoadPunchById(notification.punchId));

        this.notifications.punch(notification);
      } catch (e) {
        // Keep a minimal log for malformed messages

        console.warn('Failed to parse punch notification', e, msg.body);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
